import React from 'react';
import classNames from 'classnames';
import ShiftTimePicker from '../../../components/form/ShiftTimePicker';
import Select from '../../../components/form/Select';
import Checkbox from '../../../components/form/Checkbox';
import trashRedIcon from '../../../assets/images/trash-red-icon.svg';

// The row's ancestors trap an absolutely-positioned menu, so both dropdowns portal their menu to
// the document body. That puts it outside `.split-shift-modal`, so the modal-scoped rules no
// longer reach it: the z-index must clear the modal's own 2000, and the menu font-size (normally
// inherited from `.select-container` in the shared Select.scss) is restored here.
const MENU_PORTAL_Z_INDEX = 2100;
const MENU_PORTAL_TARGET = document.body;
const MENU_PORTAL_STYLES = {
	menuPortal: (base) => ({ ...base, zIndex: MENU_PORTAL_Z_INDEX, fontSize: '1.4rem' })
};

const SplitShiftSegmentRow = ({
	index,
	number,
	segment,
	shiftStart,
	shiftEnd,
	isFirst,
	isLast,
	canDelete,
	canCancel,
	cancelationReasonOptions,
	cancelationReasonError,
	timesDisabled,
	shouldShowUnits,
	unitOptions,
	startError,
	endError,
	onStartChange,
	onEndChange,
	onStartError,
	onEndError,
	onUnitChange,
	onDelete,
	onCancelToggle,
	onCancelationReasonChange
}) => {
	const handleStartChange = (value) => onStartChange(index, value);
	const handleEndChange = (value) => onEndChange(index, value);
	const handleStartError = () => onStartError(index);
	const handleEndError = () => onEndError(index);
	const handleUnit = (payload) => onUnitChange(index, payload);
	const handleDelete = () => onDelete(index);
	const handleCancelToggle = (isChecked) => onCancelToggle(index, isChecked);
	const handleCancelationReason = ({ cancelationReason }) =>
		onCancelationReasonChange(index, cancelationReason || null);
	const isCancelled = !!segment.isCancelled;
	const cancelCheckboxId = `split-shift-cancel-${segment.rowId}`;
	const startFieldClass = startError ? 'split-shift-calendar-invalid' : '';
	const endFieldClass = endError ? 'split-shift-calendar-invalid' : '';

	return (
		<div className="split-shift-segment-row">
			<div className="split-shift-segment-number">{number}</div>
			<div className="split-shift-segment-main">
				<div className="split-shift-segment-fields">
					<div className="split-shift-segment-field">
						<div className="split-shift-field-label">Start time</div>
						<ShiftTimePicker
							value={segment.start}
							shiftStart={shiftStart}
							shiftEnd={shiftEnd}
							disabled={isFirst || timesDisabled}
							onChange={handleStartChange}
							onInvalid={handleStartError}
							className={startFieldClass}
							panelClassName="split-shift-calendar-panel"
						/>
						{startError && <div className="split-shift-error">{startError}</div>}
					</div>
					<div className="split-shift-segment-field">
						<div className="split-shift-field-label">End time</div>
						<ShiftTimePicker
							value={segment.end}
							shiftStart={shiftStart}
							shiftEnd={shiftEnd}
							disabled={isLast || timesDisabled}
							onChange={handleEndChange}
							onInvalid={handleEndError}
							className={endFieldClass}
							panelClassName="split-shift-calendar-panel"
						/>
						{endError && <div className="split-shift-error">{endError}</div>}
					</div>
					{shouldShowUnits && (
						<div className="split-shift-segment-field">
							<div className="split-shift-field-label">
								<span>Unit</span>
								<span className="split-shift-field-optional">(Optional)</span>
							</div>

							<Select
								isSearchable
								className="split-shift-segment-unit"
								placeholder="Select..."
								name="unitId"
								value={segment.unitId || null}
								handleChange={handleUnit}
								options={unitOptions}
								menuPortalTarget={MENU_PORTAL_TARGET}
								menuPosition="fixed"
								styles={MENU_PORTAL_STYLES}
							/>
						</div>
					)}
				</div>

				<div
					className={classNames('split-shift-segment-cancel', {
						'split-shift-segment-cancel--cancelled': isCancelled,
						'split-shift-segment-cancel--disabled': !canCancel,
						'split-shift-segment-cancel--has-error': !!cancelationReasonError
					})}
				>
					<Checkbox
						id={cancelCheckboxId}
						checked={isCancelled}
						disabled={!canCancel}
						handleChange={handleCancelToggle}
					/>
					<label
						htmlFor={cancelCheckboxId}
						className={classNames('split-shift-segment-cancel-label', {
							'split-shift-segment-cancel-label--disabled': !canCancel
						})}
					>
						Mark as Cancelled
					</label>
					<div
						className={classNames('split-shift-segment-cancel-reason', {
							'split-shift-segment-cancel-reason--hidden': !isCancelled
						})}
					>
						<Select
							placeholder="Select a reason"
							name="cancelationReason"
							value={segment.cancelationReason || null}
							options={cancelationReasonOptions}
							handleChange={handleCancelationReason}
							isDisabled={!canCancel}
							hasError={!!cancelationReasonError}
							menuPortalTarget={MENU_PORTAL_TARGET}
							menuPosition="fixed"
							styles={MENU_PORTAL_STYLES}
						/>
						{cancelationReasonError && <div className="split-shift-error">{cancelationReasonError}</div>}
					</div>
				</div>
			</div>

			<div className="split-shift-segment-actions">
				{canDelete && (
					<button type="button" className="split-shift-delete" onClick={handleDelete} aria-label="Remove segment">
						<img src={trashRedIcon} alt="Remove segment" />
					</button>
				)}
			</div>
		</div>
	);
};

export default SplitShiftSegmentRow;
